import {defineMessages} from 'react-intl'

/**
 * Catálogo de la Academia (`/academy`): el listado de cursos, el reproductor de
 * lecciones, la gestión de cursos y los dos modales de cuestionario.
 *
 * Namespace propio `academy.*`. Lo transversal (Guardar, Cancelar, Nombre,
 * Descripción…) sigue en `common.*`; aquí solo va lo específico.
 *
 * No se traducen: los nombres de curso, lección y recurso, que son datos que
 * escribe la agencia.
 */
export const ACADEMY_MESSAGES = defineMessages({
  // ── Listado ──
  title: {id: 'MENU.item.academy', defaultMessage: 'Academy'},
  subtitle: {
    id: 'academy.page.subtitle',
    defaultMessage: 'Courses and training built from your Resource Center',
  },
  manageCourses: {id: 'academy.page.manageCourses', defaultMessage: 'Manage Courses'},
  searchByName: {id: 'academy.page.searchByName', defaultMessage: 'Search by name'},
  searchPlaceholder: {id: 'academy.page.searchPlaceholder', defaultMessage: 'Type to search...'},
  courseCount: {
    id: 'academy.page.courseCount',
    defaultMessage: '{count, plural, one {# course} other {# courses}}',
  },
  noCourses: {id: 'academy.page.noCourses', defaultMessage: 'No courses available'},
  noCoursesManager: {
    id: 'academy.page.noCoursesManager',
    defaultMessage: 'Create your first course from the Manage Courses page',
  },
  noCoursesUser: {
    id: 'academy.page.noCoursesUser',
    defaultMessage: 'Courses will appear here once they are published',
  },
  lessonCount: {
    id: 'academy.lessonCount',
    defaultMessage: '{count, plural, one {# lesson} other {# lessons}}',
  },
  reviewCourse: {id: 'academy.page.reviewCourse', defaultMessage: 'Review Course'},
  continueCourse: {id: 'academy.page.continue', defaultMessage: 'Continue'},
  startCourse: {id: 'academy.page.start', defaultMessage: 'Start Course'},

  errLoadingCourses: {
    id: 'academy.page.error.loading',
    defaultMessage: 'Error loading courses: {message}',
  },

  // ── Reproductor ──
  progress: {id: 'academy.player.progress', defaultMessage: 'Progress'},
  lessonsCount: {id: 'academy.player.lessonsCount', defaultMessage: 'Lessons ({count})'},
  hasQuiz: {id: 'academy.player.hasQuiz', defaultMessage: 'Has quiz'},
  noLessons: {id: 'academy.player.noLessons', defaultMessage: 'This course has no lessons yet'},
  takeQuiz: {id: 'academy.player.takeQuiz', defaultMessage: 'Take Quiz'},
  completed: {id: 'academy.player.completed', defaultMessage: 'Completed'},
  markComplete: {id: 'academy.player.markComplete', defaultMessage: 'Mark as Complete'},
  selectLesson: {id: 'academy.player.selectLesson', defaultMessage: 'Select a lesson to start'},
  nextLesson: {id: 'academy.player.nextLesson', defaultMessage: 'Next Lesson'},
  breadcrumb: {id: 'common.a11y.breadcrumb', defaultMessage: 'breadcrumb'},
  errLoadingCourse: {
    id: 'academy.player.error.loading',
    defaultMessage: 'Error loading course: {message}',
  },
  toastLessonCompleted: {
    id: 'academy.player.toast.completed',
    defaultMessage: 'Lesson completed!',
  },
  errMarkComplete: {
    id: 'academy.player.error.markComplete',
    defaultMessage: 'Error marking lesson complete',
  },

  // ── Gestión de cursos ──
  newCourse: {id: 'academy.manage.newCourse', defaultMessage: 'New Course'},
  coursesCount: {id: 'academy.manage.coursesCount', defaultMessage: 'Courses ({count})'},
  noCoursesYet: {id: 'academy.manage.noCoursesYet', defaultMessage: 'No courses yet'},
  draft: {id: 'academy.manage.draft', defaultMessage: 'Draft'},
  editCourse: {id: 'academy.manage.editCourse', defaultMessage: 'Edit course'},
  deleteCourse: {id: 'academy.manage.deleteCourse', defaultMessage: 'Delete course'},
  lessonsOf: {id: 'academy.manage.lessonsOf', defaultMessage: 'Lessons — {name}'},
  lessons: {id: 'academy.manage.lessons', defaultMessage: 'Lessons'},
  addLesson: {id: 'academy.manage.addLesson', defaultMessage: 'Add Lesson'},
  pickCourse: {id: 'academy.manage.pickCourse', defaultMessage: 'Pick a course to see its lessons'},
  noLessonsYet: {id: 'academy.manage.noLessonsYet', defaultMessage: 'No lessons yet'},
  moveUp: {id: 'academy.manage.moveUp', defaultMessage: 'Move up'},
  moveDown: {id: 'academy.manage.moveDown', defaultMessage: 'Move down'},
  optionalSuffix: {id: 'academy.manage.optionalSuffix', defaultMessage: ' · optional'},
  quizSuffix: {id: 'academy.manage.quizSuffix', defaultMessage: 'quiz'},
  editQuiz: {id: 'academy.manage.editQuiz', defaultMessage: 'Edit quiz'},
  addQuiz: {id: 'academy.manage.addQuiz', defaultMessage: 'Add quiz'},
  editLesson: {id: 'academy.manage.editLesson', defaultMessage: 'Edit lesson'},
  deleteLesson: {id: 'academy.manage.deleteLesson', defaultMessage: 'Delete lesson'},
  confirmDeleteCourse: {
    id: 'academy.manage.confirm.deleteCourse',
    defaultMessage:
      'Delete course “{name}” with all its lessons, quizzes and user progress?',
  },
  confirmDeleteLesson: {
    id: 'academy.manage.confirm.deleteLesson',
    defaultMessage: 'Delete lesson “{name}”?',
  },
  toastCourseDeleted: {id: 'academy.manage.toast.courseDeleted', defaultMessage: 'Course deleted'},
  errDeleteCourse: {
    id: 'academy.manage.error.deleteCourse',
    defaultMessage: 'Error deleting course',
  },
  toastLessonDeleted: {id: 'academy.manage.toast.lessonDeleted', defaultMessage: 'Lesson deleted'},
  errDeleteLesson: {
    id: 'academy.manage.error.deleteLesson',
    defaultMessage: 'Error deleting lesson',
  },
  errReorderLessons: {
    id: 'academy.manage.error.reorderLessons',
    defaultMessage: 'Error reordering lessons',
  },

  // ── Formulario de curso ──
  formEditCourse: {id: 'academy.courseForm.edit', defaultMessage: 'Edit Course'},
  formNewCourse: {id: 'academy.courseForm.new', defaultMessage: 'New Course'},
  coursePlaceholder: {id: 'academy.courseForm.namePlaceholder', defaultMessage: 'Course name'},
  descriptionPlaceholder: {
    id: 'academy.courseForm.descriptionPlaceholder',
    defaultMessage: 'What will users learn in this course?',
  },
  coverImage: {id: 'academy.courseForm.coverImage', defaultMessage: 'Cover Image'},
  coverPlaceholder: {
    id: 'academy.courseForm.coverPlaceholder',
    defaultMessage: 'Select an image from Resource Center...',
  },
  coverHint: {
    id: 'academy.courseForm.coverHint',
    defaultMessage: 'Optional. Shown on the course card.',
  },
  folder: {id: 'academy.courseForm.folder', defaultMessage: 'Folder'},
  noFolder: {id: 'academy.courseForm.noFolder', defaultMessage: 'No folder'},
  published: {
    id: 'academy.courseForm.published',
    defaultMessage: 'Published (visible to users)',
  },
  createCourse: {id: 'academy.courseForm.create', defaultMessage: 'Create Course'},
  toastCourseUpdated: {
    id: 'academy.courseForm.toast.updated',
    defaultMessage: 'Course updated successfully',
  },
  toastCourseCreated: {
    id: 'academy.courseForm.toast.created',
    defaultMessage: 'Course created successfully',
  },
  errSaveCourse: {id: 'academy.courseForm.error.save', defaultMessage: 'Error saving course'},

  // ── Formulario de lección ──
  formEditLesson: {id: 'academy.lessonForm.edit', defaultMessage: 'Edit Lesson'},
  formAddLesson: {id: 'academy.lessonForm.add', defaultMessage: 'Add Lesson'},
  contentLabel: {
    id: 'academy.lessonForm.contentLabel',
    defaultMessage: 'Content (Resource Center)',
  },
  contentPlaceholder: {
    id: 'academy.lessonForm.contentPlaceholder',
    defaultMessage: 'Search a resource to use as lesson content...',
  },
  contentHint: {
    id: 'academy.lessonForm.contentHint',
    defaultMessage: 'The lesson shows this resource. Users read or watch it here.',
  },
  customTitle: {id: 'academy.lessonForm.customTitle', defaultMessage: 'Custom Title'},
  customTitlePlaceholder: {
    id: 'academy.lessonForm.customTitlePlaceholder',
    defaultMessage: 'Optional: overrides the resource name in this course',
  },
  requiredToComplete: {
    id: 'academy.lessonForm.required',
    defaultMessage: 'Required to complete the course (counts toward progress)',
  },
  toastLessonUpdated: {
    id: 'academy.lessonForm.toast.updated',
    defaultMessage: 'Lesson updated successfully',
  },
  toastLessonAdded: {
    id: 'academy.lessonForm.toast.added',
    defaultMessage: 'Lesson added successfully',
  },
  errSaveLesson: {id: 'academy.lessonForm.error.save', defaultMessage: 'Error saving lesson'},
  vContentRequired: {
    id: 'academy.lessonForm.validation.content',
    defaultMessage: 'Content resource is required',
  },

  // ── Constructor de cuestionarios ──
  quizBuilderTitle: {
    id: 'academy.quizBuilder.title',
    defaultMessage: 'Quiz Builder — {lesson}',
  },
  noQuizYet: {id: 'academy.quizBuilder.noQuizYet', defaultMessage: 'This lesson has no quiz yet'},
  noQuizHint: {
    id: 'academy.quizBuilder.noQuizHint',
    defaultMessage: 'Create a quiz to evaluate users after this lesson',
  },
  createQuiz: {id: 'academy.quizBuilder.createQuiz', defaultMessage: 'Create Quiz'},
  passingScore: {id: 'academy.quizBuilder.passingScore', defaultMessage: 'Passing score (%)'},
  deleteQuiz: {id: 'academy.quizBuilder.deleteQuiz', defaultMessage: 'Delete Quiz'},
  questionsCount: {
    id: 'academy.quizBuilder.questionsCount',
    defaultMessage: 'Questions ({count})',
  },
  noOptionsYet: {
    id: 'academy.quizBuilder.noOptionsYet',
    defaultMessage: 'No options yet — add at least two',
  },
  noCorrectOption: {
    id: 'academy.quizBuilder.noCorrectOption',
    defaultMessage: 'Mark one option as the correct answer',
  },
  markCorrect: {id: 'academy.quizBuilder.markCorrect', defaultMessage: 'Mark as correct answer'},
  newOptionPlaceholder: {
    id: 'academy.quizBuilder.newOptionPlaceholder',
    defaultMessage: 'New option text...',
  },
  option: {id: 'academy.quizBuilder.option', defaultMessage: 'Option'},
  newQuestionPlaceholder: {
    id: 'academy.quizBuilder.newQuestionPlaceholder',
    defaultMessage: 'New question text...',
  },
  addQuestion: {id: 'academy.quizBuilder.addQuestion', defaultMessage: 'Add Question'},
  toastQuizCreated: {
    id: 'academy.quizBuilder.toast.created',
    defaultMessage: 'Quiz created — now add questions',
  },
  errCreateQuiz: {id: 'academy.quizBuilder.error.create', defaultMessage: 'Error creating quiz'},
  confirmDeleteQuiz: {
    id: 'academy.quizBuilder.confirm.deleteQuiz',
    defaultMessage: 'Delete this quiz with all its questions and attempts?',
  },
  toastQuizDeleted: {id: 'academy.quizBuilder.toast.deleted', defaultMessage: 'Quiz deleted'},
  errDeleteQuiz: {id: 'academy.quizBuilder.error.delete', defaultMessage: 'Error deleting quiz'},
  toastScoreUpdated: {
    id: 'academy.quizBuilder.toast.scoreUpdated',
    defaultMessage: 'Passing score updated',
  },
  errUpdateQuiz: {id: 'academy.quizBuilder.error.update', defaultMessage: 'Error updating quiz'},
  errAddQuestion: {
    id: 'academy.quizBuilder.error.addQuestion',
    defaultMessage: 'Error adding question',
  },
  confirmDeleteQuestion: {
    id: 'academy.quizBuilder.confirm.deleteQuestion',
    defaultMessage: 'Delete this question?',
  },
  errDeleteQuestion: {
    id: 'academy.quizBuilder.error.deleteQuestion',
    defaultMessage: 'Error deleting question',
  },
  errAddOption: {id: 'academy.quizBuilder.error.addOption', defaultMessage: 'Error adding option'},
  errUpdateOption: {
    id: 'academy.quizBuilder.error.updateOption',
    defaultMessage: 'Error updating option',
  },
  errDeleteOption: {
    id: 'academy.quizBuilder.error.deleteOption',
    defaultMessage: 'Error deleting option',
  },

  // ── Cuestionario del alumno ──
  quizOf: {id: 'academy.quiz.titleOf', defaultMessage: 'Quiz: {lesson}'},
  congratulations: {id: 'academy.quiz.congratulations', defaultMessage: 'Congratulations!'},
  notPassed: {id: 'academy.quiz.notPassed', defaultMessage: 'Not passed'},
  yourScore: {
    id: 'academy.quiz.yourScore',
    defaultMessage: 'Your score: <b>{score}%</b> (minimum to pass: {minimum}%)',
  },
  review: {id: 'academy.quiz.review', defaultMessage: 'Review'},
  yourAnswer: {id: 'academy.quiz.yourAnswer', defaultMessage: ' (your answer)'},
  quizIntro: {
    id: 'academy.quiz.intro',
    defaultMessage:
      '{count, plural, one {Answer the # question.} other {Answer all # questions.}} Minimum score to pass: <b>{minimum}%</b>',
  },
  tryAgain: {id: 'academy.quiz.tryAgain', defaultMessage: 'Try Again'},
  submitQuiz: {id: 'academy.quiz.submit', defaultMessage: 'Submit Quiz'},
  submitting: {id: 'academy.quiz.submitting', defaultMessage: 'Submitting...'},
  noQuestionsYet: {
    id: 'academy.quiz.noQuestionsYet',
    defaultMessage: 'This quiz has no questions yet.',
  },
  warnAnswerAll: {
    id: 'academy.quiz.warn.answerAll',
    defaultMessage: 'Please answer all questions before submitting',
  },
  toastPassed: {id: 'academy.quiz.toast.passed', defaultMessage: 'You passed with {score}%!'},
  toastFailed: {
    id: 'academy.quiz.toast.failed',
    defaultMessage: 'Score: {score}%. Try again!',
  },
  errSubmitQuiz: {id: 'academy.quiz.error.submit', defaultMessage: 'Error submitting quiz'},

  // ── Reusadas ──
  loading: {id: 'common.loading', defaultMessage: 'Loading...'},
  name: {id: 'common.field.name', defaultMessage: 'Name'},
  description: {id: 'common.field.description', defaultMessage: 'Description'},
  displayOrder: {id: 'common.field.displayOrder', defaultMessage: 'Display Order'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  save: {id: 'common.action.save', defaultMessage: 'Save'},
  saving: {id: 'common.state.saving', defaultMessage: 'Saving...'},
  saveChanges: {id: 'common.action.saveChanges', defaultMessage: 'Save changes'},
  previous: {id: 'common.action.previous', defaultMessage: 'Previous'},
  next: {id: 'common.action.next', defaultMessage: 'Next'},
  close: {id: 'common.action.close', defaultMessage: 'Close'},
  done: {id: 'common.action.done', defaultMessage: 'Done'},
  vNameRequired: {id: 'validation.name.required', defaultMessage: 'Name is required'},
})

/**
 * El backend devuelve el mensaje de error ya redactado; el prefijo es lo único
 * que se traduce. **Va dentro de `defineMessages`**: un descriptor declarado
 * fuera no lo extrae formatjs y la llave se pierde en silencio (guía §17.bis).
 */
export const BACKEND_ERROR = defineMessages({
  withMessage: {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
}).withMessage

/** Marca de campo obligatorio y separadores. Símbolos, no texto. */
export const REQUIRED_MARK = '*'
export const DOT_SEP = ' · '
/** Separador entre el numero de orden y el titulo. Simbolo. */
export const ORDINAL_SEP = '. '
/** Punto tras el numero de orden en la lista de lecciones. Simbolo. */
export const ORDINAL_DOT = '.'
export const PERCENT = '%'
